import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import styled from 'styled-components';
import { ICreatorDailyPerformanceDB } from 'type/db';

import BasicColumnChart from 'components/common/chart/BasicColumnChart';
import CreatorAverageCard from 'components/dashboard/cardinal/average/CreatorAverageCard';
import {
	useGetCreatorGroups,
	useGetCreatorGroupSummary,
	useGetCreatorMissionPerformanceDaily,
} from 'hooks/queries/creator';
import CoreRadioChips from 'shared/chips/CoreRadioChips';
import CoreLoading from 'shared/CoreLoading';
import CoreSelect from 'shared/CoreSelect';
import {
	ReportSection,
	ReportSectionCard,
	ReportSectionHeader,
	ReportSectionText,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';
import { groupOptionList, TGroupOption } from 'utils/date';

import ContentProgressCard from './content/ContentProgressCard';

const ChannelGrowth = () => {
	// 설명 groupId, platformCode, creatorPk

	const { groupId, platformCode, creatorPk } = useParams();

	const { data: creatorGroupSummary, isLoading: creatorGroupSummaryLoading } =
		useGetCreatorGroupSummary(creatorPk, groupId);

	const { data: creatorGroups } = useGetCreatorGroups(creatorPk);

	const creatorJoinGroups = creatorGroups?.map((mission, index) => ({
		label: `참여기수: ${mission.groupId}기`,
		value: `${mission.groupId}`,
	}));

	const [selectedJoinGroup, setSelectedJoinGroup] = useState('');

	// creatorGroups가 변경될 때마다 selectedJoinGroup 업데이트
	useEffect(() => {
		if (creatorJoinGroups?.length) {
			setSelectedJoinGroup(creatorJoinGroups[creatorJoinGroups.length - 1].label);
		}
	}, [creatorJoinGroups]);

	//성과 그래프
	const dataMapper: { [key: string]: keyof ICreatorDailyPerformanceDB } = {
		'종합 성과': 'totalPerformanceScore',
		'좋아요 수(릴스)': 'reelsLikeCount',
		'좋아요 수(스타일클립)': 'styleClipLikeCount',
		'댓글 수': 'commentCount',
		'조회 수': 'viewCount',
		'발송 수(소셜비즈)': 'sentCount',
		'클릭 수(소셜비즈)': 'clickCount',
	} as const;

	const [selectedMissionGraph, setSelectedMissionGraph] = useState(
		Object.keys(dataMapper)[0],
	);

	const missionOptions = useMemo(
		() =>
			creatorGroupSummary?.missionStatus.map((mission, index) => ({
				label: `${index + 1}차 미션`,
				value: `${mission.missionNumber}`,
			})) ?? [],
		[creatorGroupSummary?.missionStatus],
	);

	const [selectedMission, setSelectedMission] = useState<string>('');
	useEffect(() => {
		// 지정 날짜에 업로드(1차 미션:10일 / 2차 미션:20일 / 3차 미션:30일)
		const currentDate = new Date();
		const today = currentDate.getDate();

		// 날짜에 따라 미션 선택
		let selectedIndex = 0;
		if (today > 10) {
			selectedIndex = 1; // 3차 미션
		} else if (today > 20) {
			selectedIndex = 2; // 2차 미션
		}

		setSelectedMission(missionOptions?.[selectedIndex]?.label ?? '');
	}, [missionOptions]);

	const selectedMisssionValue = missionOptions?.find(
		(mission) => mission.label === selectedMission,
	)?.value;

	const {
		data: creatorMissionPerformanceDaily,
		isLoading: creatorMissionPerformanceLoading,
	} = useGetCreatorMissionPerformanceDaily(creatorPk, groupId, selectedMisssionValue);

	// 선택된 데이터 필터링
	const missionChartData =
		creatorMissionPerformanceDaily?.[dataMapper[selectedMissionGraph]] || [];

	const setColorType = (value: number) => {
		if (value <= 1) return 'primary';
		else if (value <= 2) return 'secondary';
		else if (value <= 3) return 'red';
		else return 'gray';
	};

	// 설명: 데이터가 없을 때
	if (!creatorGroupSummary || creatorGroupSummaryLoading)
		return (
			<ReportSection>
				<ReportSectionHeader>크리에이터 미션 성과</ReportSectionHeader>
				<Text typo="p" _margin="0 0 16px 0">
					크리에이터가 미션 참여한 콘텐츠의 좋아요, 댓글, 조회 수와 소셜비즈 발송 수, 클릭
					수 데이터를 포함합니다.
				</Text>

				<ReportSectionCard _height="396px">
					<Grid _height="100%" flex="center">
						<CoreLoading color_type="gray" />
					</Grid>
				</ReportSectionCard>
			</ReportSection>
		);

	// 설명: 데이터가 있을 때
	return (
		<ReportSection>
			<Grid flex="between">
				<Grid flex="start" align_items="start" direction="column">
					<ReportSectionHeader>크리에이터 미션 성과</ReportSectionHeader>
					<Text typo="p" _margin="0 0 16px 0">
						크리에이터가 미션 참여한 콘텐츠의 좋아요, 댓글, 조회 수와 소셜비즈 발송 수,
						클릭 수 데이터를 포함합니다.
					</Text>
				</Grid>
				<CoreSelect
					_width="210px"
					select_type={'box'}
					option_list={creatorJoinGroups?.map((option) => option.label)}
					selected_option={selectedJoinGroup}
					setSelectedOption={(e: any) => {
						setSelectedJoinGroup(e);
					}}
				/>
			</Grid>
			<Grid flex="start" _gap="16px" _width="100%" _margin="0 0 32px 0">
				<Grid flex="start" direction="column" _width="25%" _height="399px" _gap="16px">
					<ReportSectionCard _height="90px">
						<Text typo="span">기수</Text>
						<Grid>
							<Text typo="h4">
								{groupId}기{' '}
								{creatorGroupSummary?.groupMonth &&
									`${new Date(creatorGroupSummary.groupMonth).getFullYear() % 100}년 ${
										new Date(creatorGroupSummary.groupMonth).getMonth() + 1
									}월`}
							</Text>
						</Grid>
					</ReportSectionCard>
					<ReportSectionCard>
						<Grid flex="start" _gap="14px" direction="column">
							<Grid>
								<ReportSectionText typo="span" _margin="0 0 8px 0">
									총 콘텐츠
								</ReportSectionText>
								<Text typo="h4">
									{(creatorGroupSummary?.contentStatus.reels.scheduledContent ?? 0) +
										(creatorGroupSummary?.contentStatus.styleClips.scheduledContent ?? 0)}
								</Text>
							</Grid>
							<GrayLine />

							<Grid>
								<ReportSectionText typo="span" _margin="0 0 8px 0">
									릴스
								</ReportSectionText>
								<Grid flex="start" _gap="69px">
									<Grid>
										<Text typo="h4">
											{creatorGroupSummary?.contentStatus.reels.uploadedContent ?? '-'}
										</Text>
										<Text typo="span">정상 진행</Text>
									</Grid>
									<Grid>
										<Text typo="h4">
											{Math.max(
												0,
												(creatorGroupSummary?.contentStatus.reels.scheduledContent ?? 0) -
													(creatorGroupSummary?.contentStatus.reels.uploadedContent ?? 0),
											)}
										</Text>
										<Text typo="span">미수행</Text>
									</Grid>
								</Grid>
							</Grid>
							<GrayLine />

							<Grid>
								<ReportSectionText typo="span" _margin="0 0 8px 0">
									스타일클립
								</ReportSectionText>
								<Grid flex="start" _gap="69px">
									<Grid>
										<Text typo="h4">
											{creatorGroupSummary?.contentStatus.styleClips.uploadedContent}
										</Text>
										<Text typo="span">정상 진행</Text>
									</Grid>
									<Grid>
										<Text typo="h4">
											{Math.max(
												0,
												(creatorGroupSummary?.contentStatus.styleClips.scheduledContent ??
													0) -
													(creatorGroupSummary?.contentStatus.styleClips
														.uploadedContent ?? 0),
											)}
										</Text>
										<Text typo="span">미수행</Text>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</ReportSectionCard>
				</Grid>
				<Grid flex="start" _gap="16px" _width="75%">
					<ContentProgressCard />
				</Grid>
			</Grid>

			<ReportSectionCard _height="396px" _margin="0 0 32px 0">
				<Grid flex="between" _margin="0 0 16px 0">
					<CoreRadioChips
						group_type="carousel"
						style_type="default"
						_width="max-content"
						chips_list={Object.keys(dataMapper)}
						selected_chips={selectedMissionGraph}
						setSelectedChips={setSelectedMissionGraph}
					/>

					<CoreSelect
						_width="150px"
						_height="36px"
						select_type={'box'}
						option_list={missionOptions?.map((option) => option.label)}
						selected_option={selectedMission}
						setSelectedOption={(e: string) => {
							setSelectedMission(e);
						}}
					/>
				</Grid>
				<BasicColumnChart
					height="300px"
					data_name={selectedMissionGraph}
					dataLabels={false}
					datas={missionChartData.map((item) => item.value)}
					categorys={missionChartData.map((item) => item.date)}
					xTickAmount={missionChartData.length}
					zoom={false}
					trend={1}
				/>
			</ReportSectionCard>
			<Grid>
				<CreatorAverageCard />
			</Grid>
		</ReportSection>
	);
};

const { Grid, Text } = style;

const GrayLine = styled.div`
	width: 100%;
	height: 1px;
	background-color: ${({ theme }) => theme.colors.gray.gray10};
`;

export default ChannelGrowth;
